import {FormEventHandler, MouseEventHandler, PropsWithChildren, FC, CSSProperties, useCallback, useMemo} from 'react';
import './Buttons.less';
import debounce from 'debounce';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {Tooltip} from "@mui/material";
import {isMobileView} from "../../lib/picos/browserFns";
import type {IconProp} from "@fortawesome/fontawesome-svg-core";

export type ButtonProps = {
	autoFocus?: boolean;
	className?: string;
	color?: string;
	disabled?: boolean;
	label?: any;
	icon?: IconProp,
	id?: string;
	outline?: boolean;
	onClick?: MouseEventHandler<HTMLButtonElement>;
	onSubmit?: FormEventHandler<HTMLFormElement>;
	severity?: 'success' | 'warning' | 'info' | 'default' | 'danger' | 'normal',
	size?: 'small' | 'medium' | 'large',
	style?: CSSProperties,
	styleOverride?: string,
	title?: string;
	type?: 'square' | 'circle',
	route?: string,
	useDelay?: boolean
}

const Button: FC<PropsWithChildren<ButtonProps>> = props => {
	const fn = useCallback((event: any) => {
		const _doIt = () => {
			try {
				if (props.onSubmit || props.onClick) (props.onSubmit || props.onClick)(event);
			}
			catch (e) {
				console.error(e);
			}
		}

		if (props.useDelay) {
			setTimeout(_doIt, 100);
		}
		else {
			_doIt();
		}
	}, [props.onSubmit, props.onClick, props.useDelay]);

	// Leading-edge debounce: first click fires immediately; further clicks
	// within 100ms are dropped (de-dupes touchend+click and accidental
	// double-taps without making the button feel sluggish). useMemo keeps the
	// debounce instance stable across renders — wrapping the raw debounce()
	// call would create a fresh instance on every render and defeat the
	// 100ms dedup window. See OWA-397 follow-up.
	const onClick = useMemo(() => debounce(fn, 100, {immediate: true}), [fn]);

	const type = props.type ? props.type : 'square';
	const severity = props.severity && !props.color ? props.severity : '';
	const size = props.size ? props.size : 'medium';
	const icon = props.label?.type?.displayName === 'FontAwesomeIcon' ? 'icon' : '';
	const outline = props.outline ? 'outline' : '';
	const buttonType = props.onSubmit ? 'submit' : 'button';
	const styleOverride = props.styleOverride ?? '';
	const extraClassName = props.className ?? '';
	const noClick = !(props.onClick || props.onSubmit) ? 'noclick' : '';
	const className = `Button ${extraClassName} ${type} ${size} ${severity} ${icon} ${outline} ${styleOverride} ${noClick}`.trim();
	const content = <button id={props.id}
	                        type={buttonType}
	                        autoFocus={props.autoFocus}
	                        className={className}
	                        disabled={props.disabled}
	                        onClick={props.onClick
	                                 ? props.disabled
	                                   ? undefined
	                                   : onClick
	                                 : props.route
	                                   ? onClick
	                                   : undefined
	                        }
	                        onTouchEnd={props.disabled ? undefined : onClick}
	                        onSubmit={props.onSubmit
	                                  ? props.disabled
	                                    ? undefined
	                                    : onClick
	                                  : undefined}
	                        style={props.style}
	                        aria-label={props.label || props.title}
	                        aria-details={props.title}
	                        role='button'
	>
		{props.icon && <FontAwesomeIcon icon={props.icon}/>}
		{props.label || props.children}
	</button>;

	if (props.disabled) return content;
	else return <Tooltip title={isMobileView() ? null : props.title}>{content}</Tooltip>;

}

export default Button;
